import type { JSX } from "react";
import { useMemo, useRef, useState } from "react";

import { Popover as BasePopover } from "@base-ui/react/popover";
import { useControlContext } from "@frontend/contexts";

import { TimePickerContext, makeTimePickerContext } from "./TimePicker.context";
import type {
  TimePickerEndpoint,
  TimePickerProps,
  TimeRangeValue,
} from "./TimePicker.types";
import { DEFAULT_MINUTE_STEP } from "./constants";
import type { ClockTime } from "./timeMath";
import { compareTime, formatTime, parseTime, readTypedTime } from "./timeMath";

const TIME_SEPARATORS = [":", ".", "h", "H"];

function clampToStart(
  time: ClockTime,
  start: ClockTime,
  minuteStep: number,
): ClockTime {
  if (compareTime(time, start) >= 0) {
    return time;
  }

  const snapped = Math.ceil(start.minute / minuteStep) * minuteStep;

  return { hour: start.hour, minute: snapped < 60 ? snapped : start.minute };
}

export function TimePicker(props: TimePickerProps): JSX.Element {
  const {
    size,
    layout = "default",
    minuteStep = DEFAULT_MINUTE_STEP,
    disabled,
    arrow,
    open,
    defaultOpen,
    onOpenChange,
    onOpenChangeComplete,
    modal = true,
    children,
    range = false,
  } = props;

  const inherited = useControlContext();
  const resolvedSize = size ?? inherited.size;
  const pointerOffsetRef = useRef(0);

  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const isOpen = open ?? internalOpen;

  const typedRef = useRef<{ digits: string; endpoint: TimePickerEndpoint }>({
    digits: "",
    endpoint: "start",
  });

  const [typing, setTyping] = useState<{
    digits: string;
    endpoint: TimePickerEndpoint;
  } | null>(null);

  const [draft, setDraft] = useState<{
    endpoint: TimePickerEndpoint;
    time: ClockTime;
  } | null>(null);

  const isControlled = props.value !== undefined;

  const [internalValue, setInternalValue] = useState<
    string | TimeRangeValue | null
  >(props.defaultValue ?? (props.range ? [null, null] : null));

  const currentValue = isControlled ? props.value : internalValue;

  const selectedRange = useMemo<[ClockTime | null, ClockTime | null]>(
    () =>
      range
        ? [
            parseTime((currentValue as TimeRangeValue | null)?.[0]),
            parseTime((currentValue as TimeRangeValue | null)?.[1]),
          ]
        : [parseTime(currentValue as string | null), null],
    [range, currentValue],
  );

  const shownRange = useMemo<[ClockTime | null, ClockTime | null]>(() => {
    if (!draft) {
      return selectedRange;
    }

    return draft.endpoint === "start"
      ? [draft.time, selectedRange[1]]
      : [selectedRange[0], draft.time];
  }, [draft, selectedRange]);

  const [start, end] = selectedRange;
  const selected = range ? null : shownRange[0];

  function commit(next: string | TimeRangeValue | null) {
    if (!isControlled) {
      setInternalValue(next);
    }

    if (props.range) {
      props.onValueChange?.(next as TimeRangeValue);

      return;
    }

    props.onValueChange?.(next as string | null);
  }

  function selectTime(endpoint: TimePickerEndpoint, time: ClockTime) {
    if (!range) {
      commit(formatTime(time));

      return;
    }

    if (endpoint === "end") {
      commit([
        start && formatTime(start),
        formatTime(start ? clampToStart(time, start, minuteStep) : time),
      ]);

      return;
    }

    const keepEnd = null !== end && compareTime(end, time) >= 0;

    commit([formatTime(time), keepEnd ? formatTime(end) : null]);
  }

  function resetTyping() {
    typedRef.current = { digits: "", endpoint: "start" };
    setDraft(null);
    setTyping(null);
  }

  // A time being typed only lands when complete, or when the popup closes
  function commitDraft() {
    if (draft) {
      selectTime(draft.endpoint, draft.time);
    }

    resetTyping();
  }

  function handleOpenChange(
    next: boolean,
    details: Parameters<NonNullable<typeof onOpenChange>>[1],
  ) {
    if (next) {
      resetTyping();
    } else {
      commitDraft();
    }

    setInternalOpen(next);
    onOpenChange?.(next, details);
  }

  function typeKey(key: string): boolean {
    const typed = typedRef.current;
    let digits = typed.digits;

    if (/^\d$/.test(key)) {
      digits += key;
    } else if (TIME_SEPARATORS.includes(key)) {
      if (digits.length !== 1) {
        return digits.length > 1;
      }

      digits = `0${digits}`;
    } else if (key === "Backspace" && digits) {
      digits = digits.slice(0, -1);
    } else {
      return false;
    }

    if (!digits) {
      resetTyping();

      return true;
    }

    const read = readTypedTime(digits, minuteStep);

    if (!read) {
      return true;
    }

    typed.digits = digits;
    setTyping({ digits, endpoint: typed.endpoint });

    if (!read.complete) {
      setDraft({ endpoint: typed.endpoint, time: read.time });

      return true;
    }

    selectTime(typed.endpoint, read.time);
    setDraft(null);

    if (range && typed.endpoint === "start") {
      typedRef.current = { digits: "", endpoint: "end" };
      setTyping({ digits: "", endpoint: "end" });
    } else {
      typedRef.current = { digits: "", endpoint: "start" };
      setTyping(null);
      setInternalOpen(false);
    }

    return true;
  }

  function currentTime(endpoint: TimePickerEndpoint): ClockTime | null {
    if (!range) {
      return selected;
    }

    return endpoint === "start" ? start : end;
  }

  function seedTime(endpoint: TimePickerEndpoint): ClockTime | null {
    return currentTime(endpoint) ?? (endpoint === "end" ? start : null);
  }

  return (
    <TimePickerContext.Provider
      value={makeTimePickerContext({
        size: resolvedSize,
        layout,
        range,
        selected,
        selectedRange: shownRange,
        minuteStep,
        disabled,
        arrow,
        pointerOffsetRef,
        open: isOpen,
        typing,
        openPopup: () => {
          resetTyping();
          setInternalOpen(true);
        },
        typeKey,
        selectHour: (hour, endpoint = "start") => {
          selectTime(endpoint, {
            hour,
            minute: seedTime(endpoint)?.minute ?? 0,
          });
        },
        selectMinute: (minute, endpoint = "start") => {
          selectTime(endpoint, {
            hour: seedTime(endpoint)?.hour ?? 0,
            minute,
          });
        },
      })}
    >
      <BasePopover.Root
        data-slot="time-picker-root"
        open={isOpen}
        onOpenChange={handleOpenChange}
        onOpenChangeComplete={onOpenChangeComplete}
        modal={modal}
      >
        {children}
      </BasePopover.Root>
    </TimePickerContext.Provider>
  );
}
